import { AccessTime, ArrowForward, LocationOn } from "@mui/icons-material";
import {
	Box,
	Button,
	Container,
	Divider,
	Grid,
	Paper,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

// Styled components
const JobCard = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(3),
	marginBottom: theme.spacing(3),
	transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
	"&:hover": {
		transform: "translateY(-5px)",
		boxShadow: theme.shadows[4],
	},
}));

const Banner = styled(Box)(({ theme }) => ({
	background: theme.palette.primary.main,
	color: "white",
	padding: theme.spacing(4, 0),
	marginBlock: theme.spacing(2),
}));

const JobTitle = styled(Typography)(({ theme }) => ({
	fontWeight: 600,
}));

const JobMeta = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	marginBottom: theme.spacing(0.5),
	color: theme.palette.text.secondary,
	"& svg": {
		marginRight: theme.spacing(1),
		fontSize: "1rem",
	},
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
	fontWeight: 600,
	marginTop: theme.spacing(3),
	marginBottom: theme.spacing(2),
}));

const ListItem = styled(motion.li)(({ theme }) => ({
	marginBottom: theme.spacing(1),
}));

// Job data
const jobs = [
	{
		id: 1,
		title: "Veranstaltungskauffrau / Veranstaltungskaufmann",
		subtitle: "Quereinsteiger willkommen",
		location: "Vor Ort",
		type: "Vollzeit/Teilzeit",
		description:
			"Wir suchen eine engagierte und kreative Persönlichkeit zur Verstärkung unseres Teams als Veranstaltungskauffrau/-mann. Diese Position bietet sowohl Vollzeit- als auch Teilzeitmöglichkeiten, perfekt für Quereinsteiger, die eine Leidenschaft für Eventmanagement haben.",
		responsibilities: [
			"Planung und Organisation von Veranstaltungen aller Art",
			"Betreuung und Beratung unserer Kunden vor, während und nach den Veranstaltungen",
			"Koordination von Dienstleistern und Animateuren",
			"Durchführung von Außeneinsätzen auf Veranstaltungen",
		],
		requirements: [
			"Erfahrung im Bereich Eventmanagement von Vorteil, aber nicht zwingend erforderlich",
			"Hohe Kommunikations- und Organisationsfähigkeit",
			"Flexibilität und Bereitschaft zu gelegentlichen Einsätzen am Wochenende",
			"Führerschein zwingend notwendig für Außeneinsätze",
		],
		benefits: [
			"Ein dynamisches und kreatives Arbeitsumfeld",
			"Möglichkeiten zur persönlichen und beruflichen Weiterentwicklung",
			"Flexible Arbeitszeiten und ein freundliches Team",
		],
		lastWords:
			"Wenn Sie motiviert sind und neuen beruflichen Herausforderungen offen gegenüberstehen, freuen wir uns auf Ihre Bewerbung! Bitte senden Sie Ihre vollständigen Bewerbungsunterlagen an",
	},
	{
		id: 2,
		title: "Animateur/in",
		subtitle: "Perfekt als Ferienjob",
		location: "Campingplätze & Tagesveranstaltungen",
		type: "Ferienjob",
		description:
			"Sie träumen von einem spannenden Ferienjob an der malerischen Ostseeküste, der Ihnen die Möglichkeit bietet, kreative Veranstaltungen für Kinder zu organisieren? Dann ist die Position als Camping-Animatorin genau das Richtige für Sie!",
		responsibilities: [
			"Organisation und Durchführung von Veranstaltungen für Kinder",
			"Kreative Programmgestaltung",
			"Betreuung von Kindern bei Aktivitäten",
			"Mitarbeit im Animationsteam",
		],
		requirements: [
			"Freude an der Arbeit mit Kindern",
			"Kreativität und Eigeninitiative",
			"Teamfähigkeit",
			"Flexibilität",
		],
		benefits: [
			"Flexible Arbeitszeiten",
			"Attraktive Vergütung von 520,00 € pro Woche",
			"Dynamisches Team",
			"Freiheit bei der Programmgestaltung",
		],
		lastWords: "",
	},
];

export default function JobsPage() {
	const isMobile = useMediaQuery("(max-width:600px)");

	const handleApply = (e, jobId) => {
		e.stopPropagation();
		const job = jobs.find((job) => job.id === jobId);
		if (!job) return; // Ensure the job exists before proceeding

		window.open(
			`mailto:info@sofa-gmbh.de?subject=Bewerbung als ${job.title}`,
			"_blank",
		);
	};

	return (
		<Container sx={{ maxWidth: "lg", paddingInline: [1, 4] }}>
			<Banner
				component={motion.div}
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Container>
					<Typography
						variant={isMobile ? "h4" : "h2"}
						component="h1"
						align="center"
						style={{ wordBreak: "break-word" }}
					>
						Karrieremöglichkeiten
					</Typography>
				</Container>
			</Banner>

			<Grid
				container
				spacing={3}
				style={{ width: "100%", margin: "0", marginTop: "3rem" }}
			>
				{jobs.map((job, index) => (
					<Grid xs={12} key={job.id}>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: index * 0.2 }}
						>
							<JobCard elevation={1}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<JobTitle variant={isMobile ? "h5" : "h4"}>
											{job.title}
										</JobTitle>
										<Typography variant="overline">{job.subtitle}</Typography>

										<Box display="flex" flexWrap="wrap" gap={1} mb={1} mt={1}>
											<JobMeta>
												<LocationOn fontSize="small" />
												<Typography variant="body2">{job.location}</Typography>
											</JobMeta>
											<JobMeta>
												<AccessTime fontSize="small" />
												<Typography variant="body2">{job.type}</Typography>
											</JobMeta>
										</Box>

										<Typography variant="body1">{job.description}</Typography>
									</Grid>

									<Grid item xs={12}>
										<Divider sx={{ mb: 3 }} />

										<SectionTitle variant="h6">Ihre Aufgaben:</SectionTitle>
										<Box component="ul" sx={{ pl: 3, mt: 2 }}>
											{job.responsibilities.map((item, index) => (
												<ListItem
													key={item}
													initial={{ opacity: 0, x: -10 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{ duration: 0.3, delay: index * 0.1 }}
												>
													<Typography variant="body1">{item}</Typography>
												</ListItem>
											))}
										</Box>

										<SectionTitle variant="h6">Ihr Profil:</SectionTitle>
										<Box component="ul" sx={{ pl: 3, mt: 2 }}>
											{job.requirements.map((item, index) => (
												<ListItem
													key={item}
													initial={{ opacity: 0, x: -10 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{ duration: 0.3, delay: index * 0.1 }}
												>
													<Typography variant="body1">{item}</Typography>
												</ListItem>
											))}
										</Box>

										<SectionTitle variant="h6">Wir bieten:</SectionTitle>
										<Box component="ul" sx={{ pl: 3, mt: 2 }}>
											{job.benefits.map((item, index) => (
												<ListItem
													key={item}
													initial={{ opacity: 0, x: -10 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{ duration: 0.3, delay: index * 0.1 }}
												>
													<Typography variant="body1">{item}</Typography>
												</ListItem>
											))}
										</Box>

										<motion.div
											initial={{ opacity: 0, x: -10 }}
											animate={{ opacity: 1, x: 0 }}
											transition={{ duration: 0.3, delay: 0.4 }}
										>
											<Typography variant="body1" sx={{ mt: 2 }}>
												{job.lastWords}
											</Typography>
										</motion.div>

										<Box display="flex" justifyContent="flex-start" mt={4}>
											<Button
												variant="contained"
												size="large"
												endIcon={<ArrowForward style={{ color: "white" }} />}
												onClick={(e) => handleApply(e, job.id)}
											>
												Jetzt bewerben
											</Button>
										</Box>
									</Grid>
								</Grid>
							</JobCard>
						</motion.div>
					</Grid>
				))}
			</Grid>
		</Container>
	);
}
